/* Storefront integration. Never submits or repeats an add-to-cart request. */
(() => {
  'use strict';
  const host = document.getElementById('shopsta-cart');
  if (!host || window.ShopstaCart || !host.attachShadow || !window.HTMLDialogElement) return;
  const cartUrl = new URL(host.dataset.cartUrl, location.origin);
  const onCartPage = location.pathname.replace(/\/$/, '') === cartUrl.pathname.replace(/\/$/, '');
  if (onCartPage && new URLSearchParams(location.search).has('shopsta-cart-fallback')) return;
  // Restore focus after closing the drawer, but show the ring only for keyboard use.
  document.addEventListener('pointerdown', () => document.documentElement.classList.remove('shopsta-keyboard-focus'), true);
  document.addEventListener('keydown', event => {
    if (!event.metaKey && !event.ctrlKey && !event.altKey) document.documentElement.classList.add('shopsta-keyboard-focus');
  }, true);
  let loading;
  let opening;
  let pendingAdded = false;
  let trigger;

  function resource(tag, attributes, parent = document.head) {
    return new Promise((resolve, reject) => {
      const node = document.createElement(tag);
      Object.assign(node, attributes);
      const timer = setTimeout(() => reject(new Error('Cart assets timed out')), 15000);
      node.onload = () => { clearTimeout(timer); resolve(); };
      node.onerror = () => { clearTimeout(timer); reject(new Error('Cart assets unavailable')); };
      parent.append(node);
    });
  }
  async function prepareFonts(surface) {
    const configNode = host.querySelector('[data-cart-font-config]');
    if (!configNode || !window.FontFace || !document.fonts) return;
    let timer;
    try {
      const config = JSON.parse(configNode.textContent);
      if (!config.faces?.length) return;
      const faces = config.faces.map(face => new window.FontFace(face.family, `url(${JSON.stringify(face.url)})`, {
        weight: face.weight, style: 'normal', display: 'block',
      }));
      await Promise.race([
        Promise.all(faces.map(face => face.load())),
        new Promise((_, reject) => { timer = setTimeout(() => reject(new Error('Cart fonts timed out')), 1500); }),
      ]);
      // Register and select all faces together before opening; never swap visible text later.
      faces.forEach(face => document.fonts.add(face));
      surface.querySelector('.sc-dialog').dataset.scFonts = 'mixed';
    } catch (_) {
      // A font failure must not delay checkout or trigger a cart-page redirect.
      // Unregistered late responses cannot change this page's fallback typography.
    } finally {
      clearTimeout(timer);
    }
  }
  function load() {
    if (!loading) loading = (async () => {
      const surface = host.attachShadow({ mode: 'open' });
      surface.append(host.querySelector('template').content.cloneNode(true));
      const fontsReady = prepareFonts(surface);
      await Promise.all([
        resource('link', { rel: 'stylesheet', href: host.dataset.styleSrc }, surface),
        resource('script', { src: host.dataset.coreSrc }),
      ]);
      await resource('script', { src: host.dataset.drawerSrc });
      if (!window.ShopstaCartDrawer) throw new Error('Cart did not initialise');
      await fontsReady;
    })();
    return loading;
  }
  function fallback() {
    document.body.classList.remove('sc-is-open');
    // A separate, server-rendered form remains usable without any cart JavaScript.
    cartUrl.searchParams.set('shopsta-cart-fallback', '1');
    location.assign(cartUrl.href);
  }
  function open(source, added = false) {
    trigger = source || trigger || document.activeElement;
    pendingAdded ||= added;
    if (!opening) {
      opening = load().then(() => {
        const hasAdded = pendingAdded;
        pendingAdded = false;
        opening = null;
        window.dispatchEvent(new CustomEvent('shopsta:cart-opening'));
        window.ShopstaCartDrawer.open(trigger, hasAdded);
        const page = document.querySelector('[data-shopsta-cart-page]');
        if (page) {
          page.querySelector('[data-cart-fallback]').hidden = true;
          page.querySelector('[data-cart-launcher]').hidden = false;
        }
      }).catch(fallback);
    }
    return opening;
  }
  window.ShopstaCart = { open, added: source => open(source, true) };

  document.addEventListener('click', event => {
    if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest('a[href]');
    if (!link || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
    const url = new URL(link.href, location.origin);
    if (url.origin !== cartUrl.origin || url.pathname.replace(/\/$/, '') !== cartUrl.pathname.replace(/\/$/, '') || url.search || url.hash) return;
    event.preventDefault();
    void open(link);
  });

  // Sticky buttons and page builders may use their own Ajax rather than Empire.
  // Observe successful same-origin adds, preserving their promises, payloads and errors.
  function isAdd(url, method) {
    try {
      const parsed = new URL(url, location.href);
      return String(method || 'GET').toUpperCase() === 'POST' && parsed.origin === location.origin && /\/cart\/add(?:\.js)?\/?$/.test(parsed.pathname);
    } catch (_) { return false; }
  }
  function addedResponse(data) {
    if (data && !(Number(data.status) >= 400) && (data.id || Array.isArray(data.items))) window.ShopstaCart.added(document.activeElement);
  }
  const originalFetch = window.fetch;
  if (originalFetch) window.fetch = function (input, options) {
    const result = originalFetch.apply(this, arguments);
    try {
      const headers = new Headers(options?.headers || input?.headers);
      if (!headers.has('X-Shopsta-Cart') && isAdd(input?.url || input, options?.method || input?.method)) {
        result.then(response => {
          if (response.ok) return response.clone().json().then(addedResponse);
        }).catch(() => {});
      }
    } catch (_) { /* Observation must never change the caller's fetch behaviour. */ }
    return result;
  };
  if (window.XMLHttpRequest) {
    const prototype = window.XMLHttpRequest.prototype;
    const originalOpen = prototype.open;
    const originalHeader = prototype.setRequestHeader;
    const originalSend = prototype.send;
    const requests = new WeakMap();
    prototype.open = function (method, url) {
      requests.set(this, { add: isAdd(url, method), owned: false });
      return originalOpen.apply(this, arguments);
    };
    prototype.setRequestHeader = function (name) {
      const request = requests.get(this);
      if (request && String(name).toLowerCase() === 'x-shopsta-cart') request.owned = true;
      return originalHeader.apply(this, arguments);
    };
    prototype.send = function () {
      const request = requests.get(this);
      if (request?.add && !request.owned) this.addEventListener('load', () => {
        if (this.status < 200 || this.status >= 300) return;
        try { addedResponse(this.responseType === 'json' ? this.response : JSON.parse(this.responseText)); } catch (_) { /* The caller owns errors. */ }
      }, { once: true });
      return originalSend.apply(this, arguments);
    };
  }
  if (onCartPage) void open();
})();
